import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { RevealText } from './common/RevealText';
import CipherText from './common/CipherText';
import MagneticButton from './common/MagneticButton';
import Container from './common/Container';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getAllProjects } from '../redux/slice/project.slice';

const Hero = () => {
    const image = require('../assets/hero.webp');

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { projects = [] } = useSelector((state) => state.project);
    const [bgImage, setBgImage] = useState('');

    useEffect(() => {
        if (!projects || projects.length === 0) {
            dispatch(getAllProjects());
        }
    }, [dispatch, projects.length]);

    useEffect(() => {
        if (projects && projects.length > 0 && !bgImage) {
            const projectsWithImages = projects.filter(p => p.images && p.images.length > 0);
            if (projectsWithImages.length > 0) {
                const randomProject = projectsWithImages[Math.floor(Math.random() * projectsWithImages.length)];
                setBgImage(randomProject.images[0].url);
            }
        }
    }, [projects, bgImage]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.3,
                delayChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.8,
                ease: [0.22, 1, 0.36, 1]
            }
        }
    };

    const backgroundVariants = {
        hidden: { scale: 1, opacity: 0 },
        visible: {
            scale: 1.2,
            opacity: 1,
            transition: {
                opacity: { duration: 1.5, ease: [0.22, 1, 0.36, 1] },
                scale: { duration: 15, ease: "linear", repeat: Infinity, repeatType: "reverse" }
            }
        }
    };

    return (
        <div className="relative h-screen w-full bg-background">
            {/* Animated Background Image with Overlay */}
            <motion.div
                variants={backgroundVariants}
                initial="hidden"
                animate="visible"
                className="absolute inset-0 bg-cover bg-center overflow-hidden"
                style={{
                    backgroundImage: bgImage ? `url('${image}')` : `url('${bgImage}')`
                }}
            >
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 0.5 }}
                    // className="absolute inset-0 bg-gradient-to-t from-background/50 via-background/50 to-transparent"
                    className="absolute inset-0 bg-black/60"
                />
            </motion.div>

            {/* Floating Particles */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                {[...Array(6)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute w-2 h-2 bg-secondary/30 rounded-full"
                        initial={{
                            x: Math.random() * window.innerWidth,
                            y: window.innerHeight + 50,
                            opacity: 0
                        }}
                        animate={{
                            y: -50,
                            opacity: [0, 1, 0],
                        }}
                        transition={{
                            duration: Math.random() * 3 + 4,
                            repeat: Infinity,
                            delay: Math.random() * 2,
                            ease: "linear"
                        }}
                    />
                ))}
            </div>

            {/* Content */}
            <Container className="flex flex-col items-center justify-center h-full text-center">
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="w-full"
                >
                    <motion.div variants={itemVariants} className="mb-6">
                        <motion.span
                            className="block text-secondary mb-2 text-start text-4xl md:text-6xl lg:text-7xl font-serif tracking-tight"
                            initial={{ scale: 0.8, rotateX: 90 }}
                            animate={{ scale: 1, rotateX: 0 }}
                            transition={{
                                duration: 1.2,
                                delay: 0.8,
                                ease: [0.22, 1, 0.36, 1]
                            }}
                        >
                            <CipherText className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-surface italic font-medium" text="Valletta Group" />
                        </motion.span>
                        <motion.div
                            className="text-2xl md:text-3xl lg:text-4xl font-normal text-surface/70"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1.5 }}
                        >
                            <RevealText text="The Best Real-Estate Builders & Developers of Surat" />
                        </motion.div>
                    </motion.div>

                    <motion.div
                        variants={itemVariants}
                        className="mt-4 max-w-2xl text-lg md:text-xl text-light font-sans tracking-wide leading-relaxed"
                    >
                        <RevealText text="Constructing highly dynamic, convenient, and stable residential complexes since 1999." />
                    </motion.div>

                    <motion.div
                        variants={itemVariants}
                        className="mt-10 flex flex-col sm:flex-row gap-4"
                    >
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            transition={{ type: "spring", stiffness: 400, damping: 17 }}
                        >
                            <MagneticButton onClick={() => navigate('/projects')} className="px-6 md:px-8 py-3 bg-secondary hover:bg-accent text-white font-bold text-lg rounded-sm shadow-lg hover:shadow-secondary/50 transition-all duration-300">
                                View Our Projects
                            </MagneticButton>
                        </motion.div>
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            transition={{ type: "spring", stiffness: 400, damping: 17 }}
                        >
                            <MagneticButton onClick={() => navigate('/contact')} className="px-6 md:px-8 py-3 bg-transparent border-2 border-light text-light font-bold text-lg rounded-sm hover:bg-primary hover:text-white transition-colors duration-300">
                                Contact Us
                            </MagneticButton>
                        </motion.div>
                    </motion.div>

                    {/* Scroll Indicator */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 2.5 }}
                        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
                    >
                        <motion.div
                            animate={{ y: [0, 10, 0] }}
                            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                            className="flex flex-col items-center text-primary/70"
                        >
                            <span className="text-sm mb-2 font-light">Scroll Down</span>
                            <motion.div
                                className="w-6 h-10 border-2 border-primary/30 rounded-full flex justify-center"
                                whileHover={{ borderColor: "rgba(62,54,46,0.6)" }}
                            >
                                <motion.div
                                    animate={{ y: [0, 12, 0] }}
                                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                                    className="w-1 h-3 bg-primary/50 rounded-full mt-2"
                                />
                            </motion.div>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </Container>
        </div>
    );
};

export default Hero;
